import {
    Sprite,
    Container,
    Rectangle,
    Point,
    Texture,
} from 'pixi.js';
import { Game } from '../../game';
import { Settings } from '../../settings';
import { Scene } from '../Scene';
import { Grid, SimplePoint, SquareHighlight } from './Grid';
import KeyboardJS from 'keyboardjs';
import { Player } from './Player';
import { GAMEPLAY, StaticLayoutItem } from '../../constants';
import { randInt , Disposable, DisposableGroup, wait, Music, SoundEffect, DynamicCaptionInsert } from '../../utils';
import { Button, FocusManager, Focusable, fonts, InteractiveWrapper } from '../../ui';
import { Compass } from './Compass';
import { BatteryMeter } from './BatteryMeter';
import { PartsFound } from './PartsFound';
import { CoordinateMove } from './CoordinateMove';
import { CoordinateEntry } from './CoordinateEntry';
import { DwellRestArea } from './DwellRestArea';
import { CoordinateKeypad } from './CoordinateKeypad';
import { Discoverable } from './Discoverable';
import { PingAnimation } from './PingAnimation';
import { FindAnimation } from './FindAnimation';
import * as Hammer from 'hammerjs';
import * as PixiHammerConnector from 'pixi-hammer';
import { ExtPromise } from './ExtPromise';
import { Quake } from './Quake';

let SKIP_TUTORIAL = false;
try {
    const searchParams = new URLSearchParams(location.search);
    // check for skip tutorials query param
    if (searchParams.has('skipTutorial'))
    {
        SKIP_TUTORIAL = searchParams.get('skipTutorial') === 'true';
    }
}
catch (e) {}

type WallType = 'rock'|'ice'|'donut'|'log'|'flower'|'metal';
const WALL_TYPES:{[t in WallType]: {crasher: string[], crasherVo: string[], uncrashed:string, crashed:string, vo:string, sfx:string}} = {
    rock: {
        crasher: ['CaveCrasher1', 'cavecrasher_01000'],
        crasherVo: ['CYB_Game_CC1Front', 'CYB_Game_CC1Middle', 'CYB_Game_CC1Back'],
        uncrashed: '01_boulder_wall{size}.jpg',
        crashed: '01_rock_wall_crashed{size}.jpg',
        vo: 'CYB_Game_Wall_Boulders',
        sfx: 'CYB_SFX_CC-Boulders.mp3',
    },
    ice: {
        crasher: ['CaveCrasher2', 'cavecrasher_02000'],
        crasherVo: ['CYB_Game_CC2Back', 'CYB_Game_CC2Middle', 'CYB_Game_CC2Front'],
        uncrashed: '02_ice_wall{size}.jpg',
        crashed: '02_ice_wall_crashed{size}.jpg',
        vo: 'CYB_Game_Wall_Ice',
        sfx: 'CYB_SFX_CC-IceMelt.mp3',
    },
    donut: {
        crasher: ['CaveCrasher3', 'cavecrasher_03000'],
        crasherVo: ['CYB_Game_CC3Front', 'CYB_Game_CC3Middle', 'CYB_Game_CC3Back'],
        uncrashed: '03_donut_wall{size}.jpg',
        crashed: '03_donut_wall_crashed{size}.jpg',
        vo: 'CYB_Game_Wall_Donuts',
        sfx: 'CYB_SFX_CC-Chomp.mp3',
    },
    log: {
        crasher: ['CaveCrasher4', 'cavecrasher_04000'],
        crasherVo: ['CYB_Game_CC4Front', 'CYB_Game_CC4Middle', 'CYB_Game_CC4Back'],
        uncrashed: '04_camptopialog_wall{size}.jpg',
        crashed: '04_camptopialog_wall_crashed{size}.jpg',
        vo: 'CYB_Game_Wall_Logs',
        sfx: 'CYB_SFX_CC-Chainsaw.mp3',
    },
    flower: {
        crasher: ['CaveCrasher5', 'cavecrasher_05000'],
        crasherVo: ['CYB_Game_CC5Front', 'CYB_Game_CC5Middle', 'CYB_Game_CC5Back'],
        uncrashed: '05_funkyflower_wall{size}.jpg',
        crashed: '05_funkyflower_wall_crashed{size}.jpg',
        vo: 'CYB_Game_Wall_FunkyFlowers',
        sfx: 'CYB_SFX_CC-Shears.mp3',
    },
    metal: {
        crasher: ['CaveCrasher6', 'cavecrasher_06000'],
        crasherVo: ['CYB_Game_CC6Front', 'CYB_Game_CC6Middle', 'CYB_Game_CC6Back'],
        uncrashed: '06_metal_wall{size}.jpg',
        crashed: '06_metal_wall_crashed{size}.jpg',
        vo: 'CYB_Game_Wall_Metal',
        sfx: 'CYB_SFX_CC-Drill.mp3',
    },
};

const DISTRACTOR_IMGS:string[] = [];
const DISTRACTOR_VO:string[] = [];
for (let i = 0; i < 13; ++i)
{
    DISTRACTOR_IMGS.push(`distractor${String(i + 1).padStart(4, '0')}`);
    DISTRACTOR_VO.push(`CYB_Game_Distractor${i + 1}`);
};

const IS_IN: {[name:string]:string} = {
    jackie: 'CYB_Game_Jackie_Is_In',
    inez: 'CYB_Game_Inez_Is_In',
    matt: 'CYB_Game_Matt_Is_In'
};
const START_POS: {[name:string]:string} = {
    jackie: 'CYB_Game_Scene_AD_JackieStartPos',
    inez: 'CYB_Game_Scene_AD_InezStartPos',
    matt: 'CYB_Game_Scene_AD_MattStartPos'
};
const LETS_GO: {[name:string]:string} = {
    jackie: 'LetsGo_Jackie',
    inez: 'LetsGo_Inez',
    matt: 'LetsGo_Matt'
};
const CHEER: {[name:string]:string} = {
    jackie: 'Cheer_Jackie',
    inez: 'Cheer_Inez',
    matt: 'Cheer_Matt'
};
const PING_AD: {[name:string]:string} = {
    jackie: 'CYB_Game_AD_Ping_Echo_Jackie',
    inez: 'CYB_Game_AD_Ping_Echo_Inez',
    matt: 'CYB_Game_AD_Ping_Echo_Matt',
};
const PING_MISS_AD: {[name:string]:string} = {
    jackie: 'CYB_Game_AD_Ping_None_Jackie',
    inez: 'CYB_Game_AD_Ping_None_Inez',
    matt: 'CYB_Game_AD_Ping_None_Matt',
};

/** Helper point for calculations, like getting gesture events into global coordinates */
const HelperPoint = new Point();

function getCoordVO(x:number, y:number)
{
    return [{insert:'(', respectSpace: true},`CYB_Game_${GAMEPLAY.X_LABELS[x]}`, {insert:', '}, `CYB_Game_${GAMEPLAY.Y_LABELS[y]}`, {insert:') '}];
}

export class GameScene extends Scene
{
    private levelData: typeof GAMEPLAY.LEVELS[0];
    private levelNum: number;
    private background: Sprite;
    private noBackground: Sprite;
    private wallOverlay: Sprite;
    private wallButton: Button;
    private wallButtonClicked: ExtPromise<void>;
    private gestureCatcher: Container;
    private grid: Grid;
    private allHighlightCols;
    private player: Player;
    private distractors: number[];
    private partAtlas: string;
    private partNames: string[];
    private discoverables: Discoverable[];
    private pickup: Button;
    private settings: Button;
    private hint: Button;
    private restAreaItems: Focusable[] = [];
    private restArea: DwellRestArea;
    private compass: Compass;
    private battery: BatteryMeter;
    private partsFound: PartsFound;
    private coordMove: CoordinateMove;
    private keypad: CoordinateKeypad;
    private ping: PingAnimation;
    private findAnim: FindAnimation;
    private pinging = false;
    private hammer: PixiHammerConnector;
    private inactivityTimer: number;
    private emptyPickupCount: number;
    /** Only allow a single action at a given stage of the tutorial */
    private allowedAction: 'move'|'ping'|'pickup'|'coord-x'|'coord-y'|null;
    /** Tutorial line to repeat on timeout */
    private repeatLine: string|string[];
    /** Function to call on timeout (goes with repeating a tutorial line */
    private repeatFunc: () => void;
    /**
     * Which step of the tutorial we are on (includes values for both tutorials)
     */
    private tutorialStep: 'firstMove'|'ping'|'moveToTarget'|'pickup'|'coord-x'|'coord-y'|null;
    /** target position for moveToTarget */
    private tutorialTarget: SimplePoint;
    private tutorialArrow: Sprite;
    private tutorialSkip: Button;
    /** Object currently being highlightd */
    private tutorialHighlight: {highlight:boolean};

    // music stuff!
    private wallMusic: Music;
    private gameMusic: Music;
    private crashMusic: Music;

    // sound effects
    private crashEffect: SoundEffect;
    private initialVolume: number;

    // for cleanup
    private disposables: DisposableGroup;
    private endOfGame = false;

    // analytics
    private tutorialName: string;

    /**
     * @param level 0-based level index.
     */
    constructor(game:Game, level = 2)
    {
        super(game);

        this.disposables = new DisposableGroup();

        this.allowedAction = null;
        this.tutorialStep = null;
        this.repeatLine = null;
        this.repeatFunc = null;
        this.tutorialHighlight = null;
        this.inactivityTimer = 0;
        this.emptyPickupCount = 0;
        this.levelNum = level;
        this.levelData = GAMEPLAY.LEVELS[level];
        this.allHighlightCols = [];
        // pick random distractors to use
        this.distractors = [];
        while (this.distractors.length < this.levelData.DISTRACTORS)
        {
            const rand = randInt(0, 12);
            if (this.distractors.indexOf(rand) === -1)
            {
                this.distractors.push(rand);
            }
        }
        // determine exactly which machine parts to use
        this.partNames = [];
        // type matches the type of the cave walls
        const type = WALL_TYPES[this.levelData.WALL].crasher;
        this.partAtlas = type[0];
        const numParts = this.levelData.PARTS ?? 3;
        for (let i = 1; i <= numParts; ++i)
        {
            this.partNames.push(`${type[1]}${i}`);
        }
        this.game.vo.tutorial = false;
    }

    async preload()
    {
        await super.preload();
        const {loader} = this;
        // add all the assets that the scene needs directly
        loader.add('gameBG', './assets/game/bg_gamescreen{size}.jpg');
        loader.add('Wall', `./assets/game/${WALL_TYPES[this.levelData.WALL].uncrashed}`);
        loader.add('CrashedWall', `./assets/game/${WALL_TYPES[this.levelData.WALL].crashed}`);
        // load just the distractors we need for this level
        for (const distractor of this.distractors)
        {
            const img = DISTRACTOR_IMGS[distractor];
            loader.add(img, `./assets/game/${img}.png`);
        }
        // load just the machine parts we need for this level
        loader.add(this.partAtlas, `./assets/game/${this.partAtlas}{size}.json`);
        // load other bits the scene is responsible for
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');
        // load the music
        loader.add('gameMusic', './assets/music/Cyb120_Final_Stereo_Music_20191206_Game.{opus,caf,mp3}');
        loader.add('wallMusic', './assets/music/Cyb120_Final_Stereo_Music_20191206_Walls.{opus,caf,mp3}');
        loader.add('crashMusic', './assets/music/CYB120_EndSting.mp3');
        // load SFX
        loader.add('crashEffect', `./assets/sfx/${WALL_TYPES[this.levelData.WALL].sfx}`);
        // add all the assets that the classes we use want
        Button.preload();
        Grid.preload();
        Player.preload(this.levelData);
        Compass.preload();
        PartsFound.preload();
        CoordinateKeypad.preload();
        CoordinateMove.preload();
        PingAnimation.preload();
        BatteryMeter.preload();
        Discoverable.preload();
        FindAnimation.preload();
        DwellRestArea.preload();

        await loader.load();

        this.setup();
    }

    private setup()
    {
        this.game.vo.overrideInterrupt = false;
        const {loader} = this;
        const texture = loader.getTexture('gameBG');
        this.background = new Sprite(texture);
        this.addChild(this.background);
        this.background.visible = this.game.settings.showBackgroundArt.value;
        this.noBackground = new Sprite(Texture.WHITE);
        this.noBackground.width = this.background.width;
        this.noBackground.height = this.background.height;
        this.noBackground.tint = 0x000000;
        this.addChild(this.noBackground);
        this.noBackground.visible = !this.game.settings.showBackgroundArt.value;
        this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.showBackgroundArt, this.backgroundHandler.bind(this)));

        this.gestureCatcher = new Container();
        this.addChild(this.gestureCatcher);
        this.gestureCatcher.interactive = true;
        this.gestureCatcher.hitArea = new Rectangle(0, 0, 2160, 1080);

        this.grid = new Grid(this.levelData, this.game.settings);
        const area = GAMEPLAY.GRID_AREA;
        this.grid.x = area.x + (area.w - this.grid.gridWidth * this.grid.squareSize) / 2;
        this.grid.y = area.y + (area.h - this.grid.gridHeight * this.grid.squareSize) / 2;
        this.addChild(this.grid);
        this.disposables.add(new InteractiveWrapper(this.grid, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_Grid');
            },
            onFocus: async () => {
                await this.game.vo.playHighlight('CYB_Game_Grid');
            },
            onTap: () => {}
        }));

        let protectedCoord: SimplePoint;
        if (this.levelData.FIXED_START)
        {
            protectedCoord = { x: 0, y: 0 };
        }

        this.discoverables = [];

        for (const num of this.distractors)
        {
            const distractor = new Discoverable(this.grid, Discoverable.Type.Distractor, DISTRACTOR_IMGS[num]);
            distractor.foundVO = DISTRACTOR_VO[num];
            this.discoverables.push(distractor);
            const coord = this.grid.getRandomEmptyCoord(protectedCoord, StaticLayoutItem.Distractor);
            distractor.coord.x = coord.x;
            distractor.coord.y = coord.y;
            this.grid.contents[coord.x][coord.y] = distractor;
            this.grid.coordToPos(coord, distractor.position);
            this.grid.addChild(distractor);
        }
        const partVo = WALL_TYPES[this.levelData.WALL].crasherVo.slice();
        for (const name of this.partNames)
        {
            const part = new Discoverable(this.grid, Discoverable.Type.Part, name, this.partAtlas);
            part.foundVO = partVo.shift();
            this.discoverables.push(part);
            const coord = this.grid.getRandomEmptyCoord(protectedCoord, StaticLayoutItem.Part);
            part.coord.x = coord.x;
            part.coord.y = coord.y;
            this.grid.contents[coord.x][coord.y] = part;
            this.grid.coordToPos(coord, part.position);
            this.grid.addChild(part);
        }
        for (let i = 0; i < this.levelData.BATTERIES; ++i)
        {
            const battery = new Discoverable(this.grid, Discoverable.Type.Battery, 'batterypack', 'GameAtlas');
            const coord = this.grid.getRandomEmptyCoord(protectedCoord, StaticLayoutItem.Battery);
            battery.coord.x = coord.x;
            battery.coord.y = coord.y;
            this.grid.contents[coord.x][coord.y] = battery;
            this.grid.coordToPos(coord, battery.position);
            this.grid.addChild(battery);
            this.discoverables.push(battery);
        }

        // remove hidden batterypack from grid if useBattery setting is turned off 
        this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.useBattery, () => this.removeBattery()));

        this.player = new Player(this.grid);
        this.player.setCoord(protectedCoord || this.grid.getRandomEmptyCoord(null, StaticLayoutItem.Player));
        this.grid.addChild(this.player);
        this.player.on('focused', () => {
            this.announcePlayerPos();
        });

        this.ping = new PingAnimation(this.game);
        this.ping.visible = false;
        this.grid.addChild(this.ping);

        this.pickup = new Button(
            loader.getSpritesheet('GameAtlas').textures['btn_pickup'],
            {
                content: 'PICK UP',
                style: fonts.GameButton,
                offset: {x: 10, y: 0}
            }
        );
        this.addChild(this.pickup);
        this.pickup.position.set(1522, 815);
        this.disposables.add(new InteractiveWrapper(this.pickup, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_PickUp');
            },
            onFocus: async () => {
                await this.game.vo.playHighlight('CYB_Game_PickUp');
                if (this.tutorialStep === 'pickup')
                {
                    this.game.vo.playHighlight('CYB_Level1Tutorial_AD12');
                }
            },
            onTap: () => {
                this.doPickup();
            }
        }));

        this.settings = new Button(loader.getSpritesheet('SharedUI').textures['btn_settings']);
        this.settings.createHitCircle(25);
        this.addChild(this.settings);
        this.settings.position.set(1595, 930);
        this.disposables.add(new InteractiveWrapper(this.settings, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_Settings');
            },
            onTap: () => {
                this.openSettings();
            }
        }));

        this.hint = new Button(loader.getSpritesheet('SharedUI').textures['btn_hint']);
        this.hint.createHitCircle(25);
        this.addChild(this.hint);
        this.hint.position.set(1705, 930);
        this.disposables.add(new InteractiveWrapper(this.hint, {
            onHover: async() => {
                await this.game.vo.playHighlight('CYB_Game_GetHint');
            },
            onTap: () => {
                this.playHint();
            }
        }));

        this.compass = new Compass();
        this.compass.position.set(1375, 418);
        this.addChild(this.compass);
        this.compass.on('activate', this.doPing, this);
        this.disposables.add(new InteractiveWrapper(this.compass, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_SendPing');
            },
            onFocus: async () => {
                await this.game.vo.playHighlight('CYB_Game_SendPing');
            },
            onTap: () => {}
        }));
        this.disposables.add(new InteractiveWrapper(this.compass.north, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_PingNorth');
            },
            onTap: () => {}
        }));
        this.disposables.add(new InteractiveWrapper(this.compass.south, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_PingSouth');
            },
            onTap: () => {}
        }));
        this.disposables.add(new InteractiveWrapper(this.compass.east, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_PingEast');
            },
            onFocus: async () => {
                await this.game.vo.playHighlight('CYB_Game_PingEast');
                if (this.tutorialStep === 'ping')
                {
                    this.game.vo.playHighlight('CYB_Level1Tutorial_AD12');
                }
            },
            onTap: () => {}
        }));
        this.disposables.add(new InteractiveWrapper(this.compass.west, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_PingWest');
            },
            onTap: () => {}
        }));
        if(this.levelData.TUTORIAL === 'basics'){
            this.compass.visible = false;
        }

        this.battery = new BatteryMeter();
        this.battery.position.set(490, 92);
        this.addChild(this.battery);
        this.battery.visible = this.levelData.TUTORIAL === 'basics' || this.game.settings.useBattery.value;
        this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.useBattery, this.batteryHandler.bind(this)));
        this.disposables.add(new InteractiveWrapper(this.battery, {
            onHover: () => {
                if (this.battery.level > 0)
                {
                    this.game.vo.playHighlight(['CYB_Game_BatteryLife', `CYB_Game_${this.battery.level}`], false);
                }
            },
            onTap: () => {}
        }));

        if (this.levelData.TUTORIAL !== 'basics')
        {
            this.partsFound = new PartsFound(this.partNames, this.partAtlas);
            this.partsFound.position.set(1142, 92);
            this.addChild(this.partsFound);
            this.disposables.add(new InteractiveWrapper(this.partsFound, {
                onHover: () => {
                    const {count} = this.partsFound;
                    if (count < 3)
                    {
                        this.game.vo.playHighlight([`CYB_Game_${3 - count}`, (count === 2 ? 'CYB_Game_PartFind' : 'CYB_Game_PartsFind')], false);
                    }
                },
                onTap: () => {}
            }));
        }

        this.restArea = new DwellRestArea(this.game);
        this.addChild(this.restArea);
        this.restArea.position.set(1370, 1035);
        this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.showRestArea, () => this.toggleShowRestArea()));
        this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.settingsOpen, () => this.activateGameContext(0)));
        const restPieces = this.restArea.getFocusItems();
        this.restAreaItems.push(restPieces[0]);
        this.restAreaItems.push(restPieces[1]);

        if (this.levelData.COORD_MOVE)
        {
            this.coordMove = new CoordinateMove(this.grid);
            this.coordMove.position.set(1374, 237);
            this.addChild(this.coordMove);
            this.coordMove.visible = this.game.settings.allowCoordinateEntry.value;
            this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.allowCoordinateEntry, value => {
                this.coordMove.visible = value;
                // end coord tutorial immediately if we aren't playing with it
                if (!value && this.tutorialStep)
                {
                    this.clearTutorialStep();
                    this.tutorialSkip.visible = false;
                }
                // update grid box state so they can be hovered or not
                this.updateGridBoxes();
            }));

            this.disposables.add(new InteractiveWrapper(this.coordMove.xCoord, {
                onHover: () => {
                    this.game.vo.playHighlight('CYB_Game_EnterLetter');
                },
                onFocus: async () => {
                    // because the tutorial will continue immediately, don't play VO unless we aren't
                    // at that tutorial step
                    if (this.tutorialStep !== 'coord-x' || this.game.settings.enableDwellClicks.value)
                    {
                        await this.game.vo.playHighlight('CYB_Game_EnterLetter');
                        // this.game.vo.playVO('CYB_Level1Tutorial_AD12');
                    }
                },
                onTap: () => {
                    // this.game.vo.stop();
                }
            }));
            this.disposables.add(new InteractiveWrapper(this.coordMove.yCoord, {
                onHover: () => {
                    this.game.vo.playHighlight('CYB_Game_EnterNumber');
                },
                onFocus: async () => {
                    // because the tutorial will continue immediately, don't play VO unless we aren't
                    // at that tutorial step
                    if (this.tutorialStep !== 'coord-y' || this.game.settings.enableDwellClicks.value)
                    {
                        await this.game.vo.playHighlight('CYB_Game_EnterNumber');
                    }
                },
                onTap: () => {
                    // this.game.vo.stop();
                }
            }));
            this.coordMove.xCoord.on('tutorialBlocked', () => {
                this.inactivityTimer = 0;
                if (this.repeatLine)
                {
                    this.triggerRepeat();
                }
            });
            this.coordMove.yCoord.on('tutorialBlocked', () => {
                this.inactivityTimer = 0;
                if (this.repeatLine)
                {
                    this.triggerRepeat();
                }
            });

            this.keypad = new CoordinateKeypad(this.game.vo);
            this.addChild(this.keypad);

            this.keypad.on('opened', () => {
                this.enabled = false;
            });
            // when the keypad opens, do some tutorial stuff
            this.keypad.on('activated', async () => {
                this.inactivityTimer = 0;
                // only stop VO, allowing the highlight VO ("enter a letter/number") to continue
                this.game.vo.stopVO();
                // hide tutorial pointer on keypad opening
                if (this.tutorialArrow)
                {
                    this.tutorialArrow.visible = false;
                }
                if (this.tutorialStep === 'coord-x' && this.keypad.isLetters)
                {
                    this.repeatLine = 'CYB_Level4Tutorial_2C';
                    this.keypad.tutorialBlocked = true;

                    this.game.vo.playVO('CYB_Level4Tutorial_2B', true);
                }
                else if (this.tutorialStep === 'coord-y' && this.keypad.isNumbers)
                {
                    this.repeatLine = 'CYB_Level4Tutorial_3C';
                    this.keypad.tutorialBlocked = true;

                    this.game.vo.playVO('CYB_Level4Tutorial_3B', true);
                }
            });

            this.keypad.on('wrong-char', () => {
                this.inactivityTimer = 0;
                let vo:string;
                switch (this.grid.gridWidth)
                {
                    case 4:
                        vo = 'CYB_Hint_5';
                        break;
                    case 6:
                        vo = 'CYB_Hint_7';
                        break;
                    case 9:
                        vo = 'CYB_Hint_9';
                        break;
                }
                this.game.vo.playVO(vo, true);
            });
            this.keypad.on('wrong-number', () => {
                this.inactivityTimer = 0;
                let vo:string;
                switch (this.grid.gridHeight)
                {
                    case 4:
                        vo = 'CYB_Hint_6';
                        break;
                    case 6:
                        vo = 'CYB_Hint_8';
                        break;
                    case 9:
                        vo = 'CYB_Hint_10';
                        break;
                }
                this.game.vo.playVO(vo, true);
            });
            // when the keypad closes, restore the key context to gameplay
            this.keypad.on('closed', () => {
                this.inactivityTimer = 0;
                this.enabled = true;
            });

            const onCoordChange = async (entry: CoordinateEntry, wasFocused: boolean) => {
                this.game.vo.stop();
                // get the tutorial updated ASAP, in case audio is interrupted
                if (this.tutorialStep === 'coord-x' && this.coordMove.xCoord.label)
                {
                    this.clearTutorialStep();
                    this.tutorialStep = 'coord-y';
                    this.allowedAction = 'coord-y';
                    this.repeatLine = 'CYB_Level4Tutorial_3B';
                    // block x-coord from opening keypad
                    this.coordMove.yCoord.tutorialBlocked = false;
                    this.coordMove.xCoord.tutorialBlocked = true;

                    this.tutorialArrow.visible = true;
                    let target = this.coordMove.yCoord.position;
                    this.toLocal(new Point(target.x, target.y - 40), this.coordMove, this.tutorialArrow.position);
                    this.tutorialArrow.angle = 0;
                }
                else if (this.tutorialStep === 'coord-y' && this.coordMove.yCoord.label)
                {
                    this.clearTutorialStep();
                    this.tutorialStep = 'coord-y';
                    this.allowedAction = 'move';
                }
                // play back the button that was pressed
                await this.game.vo.playVO(entry.coord ? `CYB_Game_${entry.coord}` : 'CYB_Game_Clear');

                // if the entry had an assigned next focus, and the entry was focused, set the next
                // focus automatically
                if (wasFocused)
                {
                    FocusManager.instance.focused = entry.nextFocus;
                }
                if (this.tutorialStep === 'coord-x' && this.coordMove.xCoord.label)
                {
                    // if the xCoord button has focus, then the yCoord button will immediately
                    // get focus and interrupt this, so don't bother playing VO
                    if (FocusManager.instance.focused !== this.coordMove.xCoord)
                    {
                        await this.game.vo.playVO('CYB_Level4Tutorial_3');
                    }
                }
                // IMPORTANT: perform our move (if possible)
                else if (this.coordMove.full)
                {
                    this.moveToCoord(this.coordMove.currentX, this.coordMove.currentY);
                }
            };
            // add change hooks for the two coordinate entry boxes
            this.coordMove.xCoord.changeCB = async (wasFocused) => {
                await onCoordChange(this.coordMove.xCoord, wasFocused);
            };
            this.coordMove.yCoord.changeCB = async (wasFocused) => {
                await onCoordChange(this.coordMove.yCoord, wasFocused);
            };

            this.keypad.on('tutorialBlocked', () => {
                if (this.repeatLine)
                {
                    this.triggerRepeat();
                }
            });
        }

        this.findAnim = new FindAnimation();
        this.findAnim.position.set(2160 / 2, 1080 / 2);
        this.addChild(this.findAnim);

        this.tutorialArrow = new Sprite(loader.getTexture('tutorialArrow', 'GameAtlas'));
        this.tutorialArrow.visible = false;
        this.tutorialArrow.anchor.set(0.5, 1);
        this.addChild(this.tutorialArrow);

        if (this.levelData.TUTORIAL === 'basics' ||
            (this.levelData.TUTORIAL === 'coord' && this.game.settings.allowCoordinateEntry.value))
        {

            this.tutorialSkip = new Button(loader.getTexture('btn_skip', 'GameAtlas'));
            this.tutorialSkip.position.set(1435, 930);
            this.tutorialSkip.scale.set(0.75);
            this.addChild(this.tutorialSkip);
            this.disposables.add(new InteractiveWrapper(this.tutorialSkip, {
                onHover: () => {
                    this.game.vo.playHighlight('CYB_Game_Skip');
                },
                onTap: () => {
                    this.game.vo.stop();
                    this.clearTutorialStep();
                    this.tutorialSkip.visible = false;
                    this.game.logEvent("skipTutorial", this.tutorialName);
                    this.tutorialName = "";
                    if (this.levelData.TUTORIAL === 'basics')
                    {
                        this.endGame('tutorial');
                    }
                    else
                    {
                        this.coordMove.yCoord.tutorialBlocked = false;
                        this.coordMove.xCoord.tutorialBlocked = false;
                        this.keypad.tutorialBlocked = false;
                    }
                }
            }));
        }

        KeyboardJS.setContext('gameplay');
        const keyListeners = {
            left: (e:KeyboardJS.KeyEvent) => {
                e.preventDefault();
                if (this.coordMove?.visible)
                {
                    return;
                }
                e.preventRepeat();
                this.moveToCoord(-1, 0, true);
            },
            down: (e:KeyboardJS.KeyEvent) => {
                e.preventDefault();
                if (this.coordMove?.visible)
                {
                    return;
                }
                e.preventRepeat();
                this.moveToCoord(0, -1, true);
            },
            up: (e:KeyboardJS.KeyEvent) => {
                e.preventDefault();
                if (this.coordMove?.visible)
                {
                    return;
                }
                e.preventRepeat();
                this.moveToCoord(0, 1, true);
            },
            right: (e:KeyboardJS.KeyEvent) => {
                e.preventDefault();
                if (this.coordMove?.visible)
                {
                    return;
                }
                e.preventRepeat();
                this.moveToCoord(1, 0, true);
            },
            space: (e: KeyboardJS.KeyEvent) => {
                e.preventRepeat();
                this.inactivityTimer = 0;
                // don't play AD if game is busy doing something
                if (!this.enabled || this.game.vo.playing)
                {
                    return;
                }
                if (this.shouldPlayAD)
                {
                    this.playSceneAD();
                }
            }
        };

        // keys bound in a context are attached to just that context
        for (const key in keyListeners)
        {
            this.disposables.add(Disposable.fromKey(key, keyListeners[key]));
        }

        // add a listener to block unwanted scrolling via arrow keys as well (because KeyboardJS can't do it for us)
        const blocker = (ev: KeyboardEvent) => {
            // don't block if we are using coord move (and thus not arrow keys)
            if (this.coordMove?.visible) return;
            switch (ev.keyCode)
            {
                case 37:
                case 38:
                case 39:
                case 40:
                    ev.preventDefault();
                    ev.stopPropagation();
                    return false;
            }
        };
        window.addEventListener('keydown', blocker);
        this.disposables.add(new Disposable(() => {
            window.removeEventListener('keydown', blocker);
        }));

        this.hammer = new PixiHammerConnector(
            this.game.pixi.view,
            this.game.pixi.renderer.plugins.interaction,
            new Hammer(this.game.pixi.view, {
                recognizers: [
                    [Hammer.Swipe, {
                        direction: Hammer.DIRECTION_HORIZONTAL | Hammer.DIRECTION_VERTICAL,
                        velocity: 0.25, // default is 0.3 (px/ms)
                    }],
                    [Hammer.Tap]
                ]
            })
        );
        this.hammer.registerHandlerTypes(['swipe', 'tap']);
        this.gestureCatcher.on('hammer-swipe', (ev: HammerInput) => {
            if (!this.enabled || KeyboardJS.getContext() !== 'gameplay')
            {
                return;
            }
            if (this.coordMove?.visible)
            {
                return;
            }
            switch (ev.direction)
            {
                case Hammer.DIRECTION_UP:
                    this.moveToCoord(0, 1, true);
                    break;
                case Hammer.DIRECTION_DOWN:
                    this.moveToCoord(0, -1, true);
                    break;
                case Hammer.DIRECTION_LEFT:
                    this.moveToCoord(-1, 0, true);
                    break;
                case Hammer.DIRECTION_RIGHT:
                    this.moveToCoord(1, 0, true);
                    break;
            }
        });
        this.gestureCatcher.on('hammer-tap', (ev: HammerInput) => {
            if (this.enabled)
            {
                // get DOM position into PIXI global position
                this.game.pixi.renderer.plugins.interaction.mapPositionToPoint(HelperPoint, ev.center.x, ev.center.y);

                const target = this.grid.gridCoordFromGlobal(HelperPoint);
                // if clicked on the grid and not playing with coordinate entry, do a move
                if (target)
                {
                    if (!(this.coordMove?.visible))
                    {                
                        this.moveToCoord(target.x, target.y);
                    }
                    else
                    {
                        this.game.vo.playVO('CYB_Hint_20', true);
                    }
                }
                // check to see if we should play audio description
                else if (!this.game.vo.playing && this.shouldPlayAD && HelperPoint.y >= 540)
                {
                    this.playSceneAD();
                }
            }
        });

        // handle some grid stuff
        this.updateGridBoxes();
        // update state any time the dwell setting changes
        // the property subscription above for the coordinate entry property will take care of
        // calling updateGridBoxes() when that happens
        this.disposables.add(Disposable.fromPropertySubscription(this.game.settings.enableDwellClicks, () => this.updateGridBoxes()));
        // track all of the squares so that we can have them all be focusable if need be
        const allHighlights: SquareHighlight[] = [];
        const allHighlightsReversed: SquareHighlight[] = [];
        // add an InteractiveWrapper for each square
        for (let x = 0; x < this.grid.gridWidth; ++x)
        {
            for (let y = 0; y < this.grid.gridHeight; ++y)
            {
                allHighlights.push(this.grid.highlights[x][y]);
                allHighlightsReversed.push(this.grid.highlights[y][x]);
                this.disposables.add(new InteractiveWrapper(this.grid.highlights[x][y], {
                    onHover: () => {
                        // play VO, but only if dwell timers are enabled, to not be annoying
                        if (this.game.settings.enableDwellClicks.value)
                        {
                            if(!this.grid.selectingRow && !this.grid.selectingCol)
                                this.game.vo.overrideUnpause = true;
                                this.game.vo.playHighlight(getCoordVO(x, y), false);
                                this.game.vo.overrideUnpause = false;

                        }
                    },
                    onFocus: async () => {
                        // if(!this.grid.selectingRow && !this.grid.selectingCol){
                            this.game.vo.overrideUnpause = true;
                            await this.game.vo.playHighlight(getCoordVO(x, y), false);
                            this.game.vo.overrideUnpause = false;

                        // }
                    },
                    onTap: () => {
                        if(this.grid.selectingRow) 
                        {
                            this.activateColumnContext(x);
                            this.grid.selectingRow = false;
                            this.grid.selectingCol = true;
                        }
                        else {
                            if (!this.game.settings.enableDwellClicks.value)
                            {
                                this.moveToCoord(x, y);
                            }
                            else{
                                this.grid.selectingCol = false;
                                this.moveToCoord(x, y);
                            }
                            this.activateGameContext(1);
                        }
                    }
                }));
            }
        }

        // Create array of columns for grid highlights
        this.allHighlightCols = [];
        const numRows = this.grid.gridHeight;
        
        for(let i = 0; i < allHighlights.length; i+= numRows)
        {
            this.allHighlightCols.push(allHighlights.slice(i, i+numRows));
        }
               
        this.activateGameContext(0);
        
        // set up rollover audio
        if (this.levelNum > 0)
        {
            this.wallOverlay = new Sprite(loader.getTexture('Wall'));
            this.wallOverlay.anchor.set(0.5);
            this.wallOverlay.position.set(this.background.texture.width / 2, this.background.texture.height / 2);
            // make interactive to block inputs
            this.wallOverlay.interactive = true;
            this.addChild(this.wallOverlay);

            this.wallButton = new Button(
                loader.getTexture('btn_next', 'SharedUI'),
                {
                    content: 'Next',
                    style: {
                        fontName: 'Asap',
                        fontSize: 38,
                        tint: 0x000000,
                    },
                    offset: {x: -30, y: -2}
                }
            );
            this.disposables.add(new InteractiveWrapper(this.wallButton, {
                onHover: () => {
                    this.game.vo.playHighlight( "CYB_Instructions_Next" );
                },
                onTap: () => {
                    this.game.vo.stop();
                    this.wallButtonClicked.resolve();
                    if(!this.endOfGame){
                        this.game.settings.enableDwellClicks.value = false;
                    }
                    this.game.settings.gridGameScene.value = true;
                }
            }));
            this.wallButton.position.set( 2160/2, 996 );
            this.addChild(this.wallButton);
        }

        // hide compass during first tutorial
        if (this.levelData.TUTORIAL === 'basics')
        {
            this.compass.visible = false;
        }
        // For analytics; only used if we're actually in a tutorial.
        if (this.levelData.TUTORIAL)
        {
            this.tutorialName = "tutorial_" + this.levelData.TUTORIAL;
        }

        this.wallMusic = new Music(loader.getSound('wallMusic'), this.game);
        this.gameMusic = new Music(loader.getSound('gameMusic'), this.game);
        this.crashMusic = new Music(loader.getSound('crashMusic'), this.game);
        this.crashEffect = new SoundEffect(loader.getSound('crashEffect'), this.game);
        this.initialVolume = this.crashEffect.sound.volume;
        this.disposables.add(this.wallMusic, this.gameMusic, this.crashMusic, this.crashEffect);
        this.enabled = false;
    }

    public async start()
    {
        if (this.levelNum > 0)
        {
            this.wallMusic.play();
            // do intro with wall overlay
            this.wallButtonClicked = new ExtPromise();
            FocusManager.instance.activateContext([this.wallButton]);
            if(this.game.settings.showRestArea.value)
            {
                this.game.settings.enableDwellClicks.value = true;
            }

            let voProm:Promise<any> = this.game.vo.playVO(`HackerLaugh_${randInt(3,5)}`);
            // want to avoid waiting on VO
            voProm.then(() => {
                return this.game.vo.playVO(WALL_TYPES[this.levelData.WALL].vo, true);
            })
            // highlight next button if VO completes, but don't wait on VO for code
            .then(() => {
                this.wallButton.highlight = true;
            })
            .then(() => {
                if (this.shouldPlayAD)
                {
                    return this.game.vo.playVO('CYB_Game_Walls_AD', true);
                }
            });
            // wait for the button to get clicked
            await this.wallButtonClicked.promise;
            // hide the things
            FocusManager.instance.popContext();
            this.wallOverlay.visible = false;
            this.wallButton.visible = false;
            this.wallButton.highlight = false;
            // immediately kick the overlay texture off the GPU to save memory
            this.wallOverlay.texture.baseTexture.dispose();
            this.wallOverlay.texture = null;
        }

        this.gameMusic.play();

        // START GAMEPLAY
        this.enabled = true;

        // set up the tutorial flags, if relevant
        // have to do it immediately so that interrupting AD doesn't break all the things
        this.tutorialSetup();
        if (this.shouldPlayAD)
        {
            await this.playSceneAD()
        }
        // now start the tutorial (or do non-tutorial intro VO)
        this.startTutorial();
        this.game.vo.tutorial = true;

        if (!this.levelData.TUTORIAL)
        {
            this.game.vo.tutorial = false;
            await this.playTutorialEnd();
        }
    }

    private async playSceneAD()
    {
        // don't allow AD during wall overlay
        if (this.wallOverlay?.visible)
        {
            return;
        }
        // ORDER: Grid, Player pos, Battery, Parts, Coord, Compass, Pickup, Settings/info
        // followed by 'you can tab around' if not tutorial
        const vo:string[] = [];
        vo.push('CYB_Game_Scene_AD_Intro');
        // Grid
        if (this.grid.gridWidth === this.grid.gridHeight)
        {
            switch (this.grid.gridWidth)
            {
                case 4:
                    vo.push('CYB_Game_Scene_AD_GridShape_4x4');
                    break;
                case 6:
                    vo.push('CYB_Game_Scene_AD_GridShape_6x6');
                    break;
                case 9:
                    vo.push('CYB_Game_Scene_AD_GridShape_9x9');
                    break;
            }
        }
        else
        {
            vo.push('CYB_Game_Scene_AD_GridShapeRect');
        }
        if (this.levelData.TUTORIAL === 'basics')
        {
            vo.push('CYB_Game_Scene_AD_TutGridNumbers', 'CYB_Game_Scene_AD_TutGridLetters');
        }
        else
        {
            switch (this.grid.gridHeight)
            {
                case 4:
                    vo.push('CYB_Game_Scene_AD_GridHeight4');
                    break;
                case 6:
                    vo.push('CYB_Game_Scene_AD_GridHeight6');
                    break;
                case 9:
                    vo.push('CYB_Game_Scene_AD_GridHeight9');
                    break;
            }
            switch (this.grid.gridWidth)
            {
                case 4:
                    vo.push('CYB_Game_Scene_AD_GridWidth4');
                    break;
                case 6:
                    vo.push('CYB_Game_Scene_AD_GridWidth6');
                    break;
                case 9:
                    vo.push('CYB_Game_Scene_AD_GridWidth9');
                    break;
            }
        }
        // Player pos
        if (this.levelData.TUTORIAL === 'basics')
        {
            vo.push('CYB_Game_Scene_AD_PlayerPos_tut');
        }
        else
        {
            if (this.player.coord.x === 0 && this.player.coord.y === 0)
            {
                vo.push(START_POS[this.levelData.CHARACTER]);
            }
            else
            {
                // need to break up the AD so we can insert some dynamic captions real quick
                await this.game.vo.playVO(vo);
                vo.length = 0;
                await this.game.vo.playVO(this.playerPosVO, false);
            }
        }
        // Battery
        if (this.battery.visible)
        {
            if (this.levelData.TUTORIAL === 'basics')
            {
                vo.push('CYB_Game_Scene_AD_Battery_tut');
            }
            else if (this.levelNum === 1)
            {
                vo.push('CYB_Game_Scene_AD_Battery');
            }
            else
            {
                vo.push('CYB_Game_Scene_AD_Battery_Short');
            }
        }
        // Parts
        if (this.partsFound?.visible)
        {
            if (this.levelNum === 1)
            {
                vo.push('CYB_Game_Scene_AD_PartsToFind_A', `CYB_Game_${3 - this.partsFound.count}`, 'CYB_Game_Scene_AD_PartsToFind_B');
            }
            else
            {
                vo.push('CYB_Game_Scene_AD_PartsToFind_Short');
            }
        }
        // Coord (and intro to compass)
        if (this.coordMove?.visible)
        {
            vo.push('CYB_Game_Scene_AD_CoordsPing', 'CYB_Game_Scene_AD_Pickup');
        }
        else if (this.compass.visible)
        {
            // Compass & pickup
            if (this.levelData.TUTORIAL === 'basics')
            {
                vo.push('CYB_Game_Scene_AD_CompassPickup');
            }
            else if (this.levelNum === 1)
            {
                vo.push('CYB_Game_Scene_AD_Compass', 'CYB_Game_Scene_AD_Pickup');
            }
            else
            {
                vo.push('CYB_Game_Scene_AD_Compass_Short');
            }
        }

        // Settings/info
        if (this.tutorialSkip?.visible)
        {
            vo.push('CYB_Game_Scene_AD_SettingsHint_Tut');
        }
        else
        {
            vo.push('CYB_Game_Scene_AD_SettingsHint');
        }

        // Finally, do some tutorial instructional AD
        if (this.levelData.TUTORIAL === 'basics')
        {
            if (this.tutorialStep === 'firstMove')
            {
                vo.push('CYB_Game_Scene_AD_TutFirstMove');
            }
            else if (this.tutorialStep === 'moveToTarget' && this.tutorialArrow.visible)
            {
                vo.push('CYB_Level1Tutorial_AD15');
            }
        }

        try {
            await this.game.vo.playVO(vo, true);
        }
        catch (err)
        {
            console.error(err);
        }
    }

    private get playerPosVO()
    {
        return [IS_IN[this.levelData.CHARACTER], ...getCoordVO(this.player.coord.x, this.player.coord.y)];
    }

    private announcePlayerPos()
    {
        return this.game.vo.playHighlight(this.playerPosVO, false);
    }

    private tutorialSetup()
    {
        if (this.levelData.TUTORIAL === 'basics')
        {
            this.tutorialStep = 'firstMove';
            this.allowedAction = 'move';
            this.repeatLine = ['CYB_Level1Tutorial_3', 'CYB_Level1Tutorial_11'];
            this.tutorialTarget = {x: this.player.coord.x + 1, y: this.player.coord.y};
        }
        else if (this.levelData.TUTORIAL === 'coord' && !SKIP_TUTORIAL && this.coordMove?.visible)
        {
            this.tutorialStep = 'coord-x';
            this.allowedAction = 'coord-x';
            this.repeatLine = 'CYB_Level4Tutorial_2';
            // block y-coord from opening keypad
            this.coordMove.yCoord.tutorialBlocked = true;
        }
    }

    /**
     * Does not actually start tutorial if it is not appropriate to do so!
     */
    private async startTutorial()
    {
        if (this.levelData.TUTORIAL === 'basics')
        {
            this.game.settings.gridGameScene.value = true;

            this.game.logEvent("tutorial_begin", this.tutorialName);
            await this.game.vo.playVO(['CYB_Level1Tutorial_Intro', 'CYB_Level1Tutorial_1'], true);

            // only continue if the tutorial didn't advance (shouldn't get to this point, but just
            // in case)
            if (this.tutorialStep === 'firstMove')
            {
                this.tutorialArrow.visible = true;
                const target = this.grid.coordToPos({x: this.player.coord.x + 1, y: this.player.coord.y});
                this.toLocal(new Point(target.x, target.y - 25), this.grid, this.tutorialArrow.position);
                this.tutorialArrow.angle = 0;

                this.tutorialHighlight = this.grid.highlights[this.player.coord.x + 1][this.player.coord.y];
                this.tutorialHighlight.highlight = true;

                await this.game.vo.playVO(['CYB_Level1Tutorial_2'], true);
                if (this.shouldPlayAD)
                {
                    await this.game.vo.playVO('CYB_Game_Pointer_AD_TutFirstMove', true);
                }
            }
        }
        else if (this.levelData.TUTORIAL === 'coord' && !SKIP_TUTORIAL && this.coordMove?.visible)
        {
            this.game.logEvent("tutorial_begin", this.tutorialName);
            if (this.tutorialStep === 'coord-x')
            {
                this.tutorialArrow.visible = true;
                let target = this.coordMove.xCoord.position;
                this.toLocal(new Point(target.x, target.y - 40), this.coordMove, this.tutorialArrow.position);
                this.tutorialArrow.angle = 0;

                await this.game.vo.playVO(['CYB_Level4Tutorial_1', 'CYB_Level4Tutorial_2'], true);

                if (this.shouldPlayAD)
                {
                    this.game.vo.playVO('CYB_L4_Tutorial_Coordinate1_AD', true);
                }
            }
        }
        else
        {
            if (this.levelData.TUTORIAL === 'coord' && !this.coordMove?.visible)
            {
                // Logging start of level/round
                // For this specific level (with the coord tutorial) if the coordMove causes
                // the tutorial not to play, we need to log the start of the level, which otherwise
                // is logged in playTutorialEnd().
                this.game.logEvent("startLevel", this.levelNum.toString());
            }

            // no tutorial!
            this.game.vo.playVO('CYB_Game_IntroEcholocation', true);
        }
    }

    private async playTutorialEnd()
    {
        const onInterrupt = () => this.clearTutorialStep();

        if (this.levelData.TUTORIAL !== 'basics')
        {
            // Logging start of level/round
            this.game.logEvent("startLevel", this.levelNum.toString());

            await this.game.vo.playVO(LETS_GO[this.levelData.CHARACTER], true);
        }

        this.tutorialArrow.visible = true;
        let target = this.settings.position;
        this.toLocal(new Point(target.x, target.y - 40), this, this.tutorialArrow.position);
        this.tutorialArrow.angle = 0;
        this.tutorialHighlight = this.settings;
        this.settings.highlight = true;
        if (this.shouldPlayAD)
        {
            await this.game.vo.playVO('CYB_Game_AD_SettingsButton', true, onInterrupt);
        }
        await this.game.vo.playVO('CYB_Game_ChangeSetup', true, onInterrupt);

        this.tutorialArrow.visible = true;
        target = this.hint.position;
        this.toLocal(new Point(target.x, target.y - 40), this, this.tutorialArrow.position);
        this.tutorialArrow.angle = 0;
        this.tutorialHighlight = this.hint;
        this.hint.highlight = true;
        this.settings.highlight = false;
        if (this.shouldPlayAD)
        {
            await this.game.vo.playVO('CYB_Game_AD_HintButton', true, onInterrupt);
        }
        await this.game.vo.playVO('CYB_Game_NeedHelp', true, onInterrupt);

        this.clearTutorialStep();
        if (this.shouldPlayAD)
        {
            await this.game.vo.playVO('CYB_Game_AD_Explore', true);
        }
    }

    private clearTutorialStep()
    {
        this.tutorialStep = null;
        this.allowedAction = null;
        this.repeatLine = null;
        this.repeatFunc = null;
        this.tutorialTarget = null;
        this.tutorialArrow.visible = false;
        if (this.tutorialHighlight)
        {
            this.tutorialHighlight.highlight = false;
            this.tutorialHighlight = null;
        }

    }

    /**
     * Updates the focus state of all of the grid's highlight boxes
     * based on settings.
     */
    private updateGridBoxes()
    {
        const coord = this.levelData.COORD_MOVE && this.game.settings.allowCoordinateEntry.value;
        const dwell = this.game.settings.enableDwellClicks.value;
        const hoverEnabled = !coord;
        const grid = this.grid.highlights;
        
        for (let x = 0; x < this.grid.gridWidth; ++x)
        {
            for (let y = 0; y < this.grid.gridHeight; ++y)
            {
                const square = grid[x][y];
                square.focusable = hoverEnabled;
                square.interactive = hoverEnabled;
            }
        }
        this.toggleShowRestArea();
    }

    // show/hide switch access rest area if setting is turned off 
    private toggleShowRestArea()
    {
        const showRestArea = this.game.settings.showRestArea.value;

        if(showRestArea) 
        { 
            this.restArea.visible = true; 
        }
        else { 
            this.restArea.visible = false; 
        }
    }

    // remove hidden batterypack from grid if useBattery setting is turned off 
    private removeBattery()
    {
        if(!this.game.settings.useBattery.value)
        {
            for (let i = 0; i < this.discoverables.length; i++)
            {
                if ((this.discoverables[i].type === Discoverable.Type.Battery) && !this.game.settings.useBattery.value)
                {
                    this.discoverables.splice(i,1);
                }
            }
            for (let i = 0; i < this.grid.contents.length; i++)
            {
                for (let j = 0; j < this.grid.contents.length; j++)
                {
                    if(this.grid.contents[i][j] !== null && this.grid.contents[i][j] !== undefined)
                    {
                        if(this.grid.contents[i][j].type == Discoverable.Type.Battery)
                        {
                            this.grid.highlights[i][j].show = false;
                            this.grid.contents[i][j].state = Discoverable.State.Found;
                            this.grid.contents[i][j] = null;
                        }
                    }
                }
            }
        }
    }

    private openSettings()
    {
        this.game.settings.settingsOpen.value = false;
        if (!this.settings.enabled)
        {
            return;
        }
        this.inactivityTimer = 0;
        const hadAD = this.shouldPlayAD;

        // Stop vo (in case high-priority vo is playing that the accessibility popover can't interrupt )
        this.game.vo.stop();

        KeyboardJS.setContext('settings');
        this.game.showAccessibilityPopover(() => {
            KeyboardJS.setContext('gameplay');

            // if audio descriptions were turned on, play the level description now
            if (!hadAD && this.shouldPlayAD)
            {
                this.playSceneAD();
            }
        });
    }

    public update(elapsed:number)
    {
        if (KeyboardJS.getContext() === 'gameplay' && !this.player.isMoving && !this.pinging &&
            !this.game.vo.playing && this.enabled)
        {
            this.inactivityTimer += elapsed;
            // always use 15 second timeouts for tutorials
            const timerTarget = this.repeatLine ? 15000 : GAMEPLAY.INACTIVITY_TIMER[this.hintLevel];
            if (this.inactivityTimer >= timerTarget)
            {
                this.inactivityTimer = 0;
                this.triggerRepeat();
            }
        }
    }

    /**
     * Triggers tutorial repeated audio or inaction hint.
     */
    private triggerRepeat()
    {
        // default is normal movement hint
        let vo:string|string[];
        if (this.hintLevel !== Settings.HintFrequency.NONE)
        {
            vo = 'CYB_Hint_1';
            // if using coordinate movement, that should be used instead of the default movement
            if (this.coordMove?.visible)
            {
                vo = 'CYB_Hint_2';
            }
            // if coordinate input is partially full, override with special VO for that
            if (this.coordMove?.partiallyFull)
            {
                vo = 'CYB_Level4_KeypadOpen';
            }
        }
        // always override with the tutorial line if present
        if (this.repeatLine)
        {
            vo = this.repeatLine;
            this.repeatFunc?.();
        }
        if (vo)
        {
            this.game.vo.playVO(vo, true);
        }
    }

    private playHint()
    {
        if (!this.enabled) return;
        this.game.vo.stop();
        // always override with the tutorial line if present
        if (this.repeatLine)
        {
            this.repeatFunc?.();
            this.game.vo.playVO(this.repeatLine, true);
            return;
        }

        // if there is a located item, tell them to go pick it up
        const located = this.grid.getContents(d => d.state === Discoverable.State.Located);
        if (located.length && this.hintLevel !== Settings.HintFrequency.NONE)
        {
            const item = located[randInt(0, located.length - 1)];
            let coordHint = true;
            // if we can do a direct "X square(s) to the N/W/E/S" hint, do that half the time
            if ((item.coord.x === this.player.coord.x || item.coord.y === this.player.coord.y)
                && Math.random() < 0.5)
            {
                coordHint = false;
                // player is on item
                if (item.coord.x === this.player.coord.x && item.coord.y === this.player.coord.y)
                {
                    this.game.vo.playVO('CYB_Game_Pingblocked', false);
                }
                // player is on same row (hint is N/S)
                else if (item.coord.x === this.player.coord.x)
                {
                    const intro = Math.random() < 0.5 ? 'CYB_Hint_25A' : 'CYB_Hint_26A';
                    const dir = item.coord.y < this.player.coord.y ? 'CYB_Game_South' : 'CYB_Game_North';
                    const dist = Math.abs(item.coord.y - this.player.coord.y);
                    this.game.vo.playVO([
                        intro,
                        `CYB_Game_${dist}`,
                        (dist === 1 ? 'CYB_Game_ObjectDetected1BB' : 'CYB_Game_ObjectDetected_pt2'),
                        dir],
                        false);
                }
                // player is on same column (hint is E/W)
                else
                {
                    const intro = Math.random() < 0.5 ? 'CYB_Hint_25A' : 'CYB_Hint_26A';
                    const dir = item.coord.x < this.player.coord.x ? 'CYB_Game_West' : 'CYB_Game_East';
                    const dist = Math.abs(item.coord.x - this.player.coord.x);
                    this.game.vo.playVO([
                        intro,
                        `CYB_Game_${dist}`,
                        (dist === 1 ? 'CYB_Game_ObjectDetected1BB' : 'CYB_Game_ObjectDetected_pt2'),
                        dir],
                        false);
                }
            }
            if (coordHint)
            {
                const square = getCoordVO(item.coord.x, item.coord.y);
                if (Math.random() < 0.5)
                {
                    this.game.vo.playVO(['CYB_Hint_15_part1', ...square, 'CYB_Hint_15_part2'], false);
                }
                else
                {
                    this.game.vo.playVO(['CYB_Hint_16', ...square], false);
                }
            }
            return;
        }

        // see if we should give actual help in the form of a partial location
        const chance = GAMEPLAY.HINT_LOCATION_CHANCE[this.hintLevel];
        if (Math.random() < chance)
        {
            const extraHelp = this.battery.visible && this.battery.level < 3;
            this.game.vo.playVO(this.getLocationHint(extraHelp), false);
            return;
        }

        // otherwise, a controls reminder or encouragement
        let options: string[] = [
            // use correct hint for movement style
            this.coordMove?.visible ? 'CYB_Hint_2' : 'CYB_Hint_1'
        ];
        if (this.hintLevel === Settings.HintFrequency.EXTRA)
        {
            options.push(
                'CYB_Hint_17',
                'CYB_Hint_18',
                'CYB_Hint_21',
            );
            switch (this.partsFound?.count)
            {
                case 0:
                    options.push('CYB_Hint_22');
                    break;
                case 1:
                    options.push('CYB_Hint_24');
                    break;
                case 2:
                    options.push('CYB_Hint_23');
                    break;
            }
        }
        const vo = options[randInt(0, options.length - 1)];
        this.game.vo.playVO(vo, true);
    }

    private getLocationHint(justParts = false)
    {
        const items = this.grid.getContents(d => d.state === Discoverable.State.Hidden && (d.type === Discoverable.Type.Part || !justParts));
        const item = items[randInt(0, items.length - 1)];
        if (randInt(0, 1) === 0)
        {
            // column
            return ['CYB_Hint_14', `CYB_Game_${GAMEPLAY.X_LABELS[item.coord.x]}`];
        }
        else
        {
            // row
            return ['CYB_Hint_13', `CYB_Game_${GAMEPLAY.Y_LABELS[item.coord.y]}`];
        }
    }

    private async doPing(direction: 'N'|'S'|'E'|'W') {
        this.activateGameContext(0);
        this.game.vo.stop();

        this.inactivityTimer = 0;
        FocusManager.instance.clearFocus();

        if (this.battery.visible && this.battery.level <= 0)
        {
            this.game.vo.playVO('CYB_Game_Transition1', true);
            return;
        }

        // bail if tutorial is blocking us or wrong direction chosen
        if (this.allowedAction && (this.allowedAction !== 'ping' || direction !== 'E'))
        {
            if (this.repeatLine)
            {
                this.triggerRepeat();
            }
            return;
        }

        if (this.tutorialArrow)
        {
            this.tutorialArrow.visible = false;
        }

        const result = this.grid.ping(this.player.coord, direction, d => d.type === Discoverable.Type.Battery && !this.battery.visible);
        let dist:number;
        if (!result.found)
        {
            // if no distance, we hit a wall, don't show anim (no room)
            if (!result.coordDist)
            {
                this.game.vo.playVO('CYB_Game_NoObjectDetected', true);
                return;
            }
            dist = result.coordDist * this.grid.squareSize;
        }
        else
        {
            dist = (result.coordDist - 1) * this.grid.squareSize;
        }
        let offsetX = 0;
        let offsetY = 0;
        let angle: number;
        switch (direction)
        {
            case 'N':
                offsetY = -this.grid.squareSize / 2;
                angle = -90;
                break;
            case 'S':
                offsetY = this.grid.squareSize / 2;
                angle = 90;
                break;
            case 'E':
                offsetX = this.grid.squareSize / 2;
                angle = 0;
                break;
            case 'W':
                offsetX = -this.grid.squareSize / 2;
                angle = 180;
                break;
        }
        this.ping.reposition(this.player.x + offsetX, this.player.y + offsetY, angle);

        // battery will be hidden if infinite battery is turned on
        if (this.battery.visible)
        {
            --this.battery.level;
        }
        this.pinging = true;
        this.enabled = false;
        this.compass.setActive(direction);

        let voProm: Promise<any>;
        if (this.shouldPlayAD)
        {
            if (this.tutorialStep === 'ping')
            {
                voProm = this.game.vo.playVO(['CYB_Level1Tutorial_AD13'], true);
            }
            else if (result.found)
            {
                voProm = this.game.vo.playVO([PING_AD[this.levelData.CHARACTER], ...getCoordVO(result.coord.x, result.coord.y)], false);
            }
            else
            {
                voProm = this.game.vo.playVO(PING_MISS_AD[this.levelData.CHARACTER], true);
            }
        }
        else
        {
            voProm = Promise.resolve();
        }

        this.ping.visible = true;
        const [hit, back] = this.ping.play(dist, this.grid.squareSize);
        back.then(() => this.ping.visible = false);

        await hit;

        if (result.found)
        {
            result.found.state = Discoverable.State.Located;
            this.grid.highlights[result.coord.x][result.coord.y].show = true;
        }

        this.compass.setActive();
        this.pinging = false;
        this.enabled = !!result.found || this.battery.level > 0 || !!this.locatedCount;

        // set up the next tutorial step immediately
        if (result.found && this.tutorialStep === 'ping')
        {
            this.clearTutorialStep();
            this.tutorialStep = 'moveToTarget';
            this.allowedAction = 'move';
            this.tutorialTarget = result.coord;
            this.repeatLine = ['CYB_Level1Tutorial_9', 'CYB_Level1Tutorial_11'];

            // point at pinged thing
            this.tutorialArrow.visible = true;
            let target = result.found.position;
            this.toLocal(new Point(target.x, target.y - 25), this.grid, this.tutorialArrow.position);
            this.tutorialArrow.angle = 0;
        }
        // await back;

        // wait for audio description to finish
        await voProm;

        if (result.found)
        {
            // because we allowed skipping the rest of the ping, ensure that we only continue if the tutorial hasn't advanced, just in case something goes horribly wrong
            if (this.tutorialStep === 'moveToTarget')
            {
                await this.game.vo.playVO('CYB_Level1Tutorial_6', true);

                // update the arrow if we haven't moved onto the next step
                if (this.tutorialStep !== 'moveToTarget')
                {
                    return;
                }
                // point at battery UI
                this.tutorialArrow.visible = true;
                let target = this.battery.position;
                this.toLocal(new Point(target.x + 100, target.y + 75), this, this.tutorialArrow.position);
                this.tutorialArrow.angle = 180;

                await this.game.vo.playVO('CYB_Level1Tutorial_7', true);

                // point back at the target position, if still on that step
                if (this.tutorialStep !== 'moveToTarget')
                {
                    return;
                }
                target = result.found.position;
                this.toLocal(new Point(target.x, target.y - 25), this.grid, this.tutorialArrow.position);
                this.tutorialArrow.angle = 0;

                this.tutorialHighlight = this.grid.highlights[result.coord.x][result.coord.y];
                this.tutorialHighlight.highlight = true;

                await this.game.vo.playVO('CYB_Level1Tutorial_8', true);

                if (this.shouldPlayAD)
                {
                    await this.game.vo.playVO(['CYB_Level1Tutorial_AD14', 'CYB_Level1Tutorial_AD15'], true);
                }

                // don't do any of the other VO
                return;
            }
            else
            {
                if (result.coordDist === 0)
                {
                    await this.game.vo.playVO('CYB_Game_Pingblocked');
                }
                else
                {
                    let detectedVO: (string|DynamicCaptionInsert)[] = [];
                    if (Math.random() < 0.5)
                    {
                        let numVO: string = `CYB_Game_${result.coordDist}`;
                        let dirVO: string;
                        switch (direction)
                        {
                            case 'N':
                                dirVO = 'CYB_Game_North';
                                break;
                            case 'E':
                                dirVO = 'CYB_Game_East';
                                break;
                            case 'W':
                                dirVO = 'CYB_Game_West';
                                break;
                            case 'S':
                                dirVO = 'CYB_Game_South';
                                break;
                        }
                        detectedVO.push('CYB_Game_ObjectDetected_pt1', numVO, (result.coordDist === 1 ? 'CYB_Game_ObjectDetected1BB' : 'CYB_Game_ObjectDetected_pt2'), dirVO, 'CYB_Game_ObjectDetected_pt3');
                    }
                    else
                    {
                        detectedVO.push('CYB_Game_ObjectDetected_ALT_pt1', ...getCoordVO(result.coord.x, result.coord.y), 'CYB_Game_ObjectDetected_ALT_pt2');
                    }

                    await this.game.vo.playVO(detectedVO, false);
                }
            }
        }
        else
        {
            const vo = Math.random() < 0.5 ? 'CYB_Game_NoObjectDetected' : 'CYB_Game_NoObjectDetected_ALT';
            await this.game.vo.playVO(vo, true);
        }
        if (!this.tutorialStep && this.game.settings.useBattery.value)
        {
            if (this.battery.level > 0)
            {
                await this.game.vo.playVO(['CYB_Game_BatteryLife', `CYB_Game_${this.battery.level}`], false);
            }
            if (this.battery.level === 3)
            {
                await this.game.vo.playVO('CYB_Game_BatteryLow', true);
            }
            else if (this.battery.level === 2)
            {
                await this.game.vo.playVO(this.getLocationHint(true), false);
            }
            else if (this.battery.level === 0 && this.locatedCount === 0)
            {
                this.endGame(false);
                return;
            }
        }
        if (this.shouldPlayAD)
        {
            await this.announcePlayerPos();
        }
    }

    /**
     * Activates the context for keyboardjs. Order depends on whether or not user is exiting the grid
     */
    private async activateGameContext(exitingGrid: number) {
        const restAreaFocusables = this.restArea.getFocusItems();
        let compassFocus = this.compass;
        let gridFocus = null;
        if(!this.restArea.visible)
        {
           restAreaFocusables[0] = null;
           restAreaFocusables[1] = null;
        }
        else {
            restAreaFocusables[0] = this.restAreaItems[0];
            restAreaFocusables[1] = this.restAreaItems[1];
        }

        if(!this.levelData.COORD_MOVE)
        {
            gridFocus = this.grid;
        }
        else {
            gridFocus = null;
        }

        if(!this.compass.visible)
        {
            compassFocus = null;
        }
        else{
            compassFocus = this.compass;
        }

        if(exitingGrid == 1){
            FocusManager.instance.activateContext([
                compassFocus,                
                this.pickup,
                this.player,
                // ...allHighlightRows,
                this.partsFound,
                this.battery,
                // (Invisible) Skip Navigation
                this.hint,
                this.settings,
                this.tutorialSkip,
                restAreaFocusables[0],
                restAreaFocusables[1],
                this.coordMove?.xCoord,
                this.coordMove?.yCoord,
                gridFocus
            ].filter(item => !!item));
        }
        else {
            FocusManager.instance.activateContext([
                this.coordMove?.xCoord,
                this.coordMove?.yCoord,
                gridFocus,
                compassFocus,
                this.pickup,
                this.player,
                // ...allHighlightRows,
                this.partsFound,
                this.battery,
                // (Invisible) Skip Navigation
                this.hint,
                this.settings,
                this.tutorialSkip,
                restAreaFocusables[0],
                restAreaFocusables[1]
                // CC Toggle
                // Close button
            ].filter(item => !!item));
        }
    }

    /**
     * Activates the context to select a column in the grid
     */
    private async activateColumnContext(y: number) {
        FocusManager.instance.activateContext(this.allHighlightCols[y]);
    }

    private async moveToCoord(x:number, y:number, relative = false)
    {
        this.inactivityTimer = 0;
        FocusManager.instance.clearFocus();

        if (!this.enabled)
        {
            return;
        }
        this.game.vo.stop();
        // bail if tutorial is blocking us
        if (this.allowedAction && this.allowedAction !== 'move')
        {
            if (this.repeatLine)
            {
                this.triggerRepeat();
            }
            return;
        }
        // if we are supposed to move to the right, prevent anything other than that
        if (this.allowedAction === 'move' &&
            // limit tutorial steps for this check
            (this.tutorialStep === 'moveToTarget' || this.tutorialStep === 'firstMove') &&
            (
                // block swipes/arrow keys to anything other than the right
                (relative && x !== 1) ||
                // block grid taps anywhere but our target
                (!relative && (x !== this.tutorialTarget.x || y !== this.tutorialTarget.y)))
            )
        {
            if (this.repeatLine)
            {
                this.triggerRepeat();
            }
            return;
        }
        // if a relative move (swipe, arrow keys) and forcing coordinate movement, not allowed!
        if (relative && this.coordMove?.visible)
        {
            this.game.vo.playVO('CYB_Hint_20', true);
            return;
        }

        if (!relative && x === this.player.coord.x && y === this.player.coord.y)
        {
            const vo = this.coordMove?.visible ? 'CYB_Game_CoordinateSpot' : 'CYB_Hint_1';
            this.game.vo.playVO(vo, true);
            return;
        }
        this.enabled = false;
        await this.player.moveToCoord(x, y, relative);
        this.coordMove?.clear();
        // update grid highlights, because we want to disable the one under the player
        this.updateGridBoxes();

        // now we can enable the game because tutorial state will be updated immediately (even if VO is cancelled).
        this.enabled = true;

        let wasCoordY = false;
        if (this.tutorialStep === 'firstMove')
        {
            this.clearTutorialStep();
            this.compass.visible = true;
            this.activateGameContext(0);
            this.allowedAction = 'ping';
            this.tutorialStep = 'ping';
            this.repeatLine = 'CYB_Level1Tutorial_5';
            this.repeatFunc = () => {
                this.tutorialHighlight = this.compass.east;
                this.tutorialHighlight.highlight = true;
            };
        }
        else if (this.tutorialStep === 'moveToTarget' && this.grid.contents[this.player.coord.x][this.player.coord.y]?.state === Discoverable.State.Located)
        {
            this.clearTutorialStep();
            this.tutorialStep = 'pickup';
            this.allowedAction = 'pickup';
            this.repeatLine = 'CYB_Level1Tutorial_12';
            this.repeatFunc = () => {
                // point at pinged thing
                this.tutorialArrow.visible = true;
                let target = this.pickup.position;
                this.toLocal(new Point(target.x - 50, target.y - 25), this, this.tutorialArrow.position);
                this.tutorialArrow.angle = 0;

                this.tutorialHighlight = this.pickup;
                this.tutorialHighlight.highlight = true;
            }
        }
        else if (this.tutorialStep === 'coord-y')
        {
            // end tutorial
            this.clearTutorialStep();
            this.coordMove.yCoord.tutorialBlocked = false;
            this.coordMove.xCoord.tutorialBlocked = false;
            this.keypad.tutorialBlocked = false;
            this.tutorialSkip.visible = false;
            // record that we did this
            wasCoordY = true;
        }

        const located = this.grid.getContents(d => d.state === Discoverable.State.Located);
        let onLocated = false;
        for (const item of located)
        {
            if (item.coord.x === this.player.coord.x && item.coord.y === this.player.coord.y)
            {
                onLocated = true;
                break;
            }
        }
        // per Loren: coordinates should be announced with every move
        if (!this.tutorialStep && onLocated)
        {
            await this.game.vo.playVO(['CYB_Game_ObjectSpot', ...getCoordVO(this.player.coord.x, this.player.coord.y)], false);
        }
        else if (this.shouldPlayAD)
        {
            await this.announcePlayerPos();
        }
        else
        {
            await this.game.vo.playVO(getCoordVO(this.player.coord.x, this.player.coord.y), false);
        }

        // if we were on firstMove (updated to ping) and are still on that step
        if (this.tutorialStep === 'ping')
        {
            if (this.shouldPlayAD)
            {
                await this.game.vo.playVO('CYB_Game_Scene_AD_CompassPickup', true);
            }
            this.tutorialArrow.visible = true;
            const target = this.compass.east.position;
            this.toLocal(new Point(target.x, target.y - 25), this.compass, this.tutorialArrow.position);
            this.tutorialArrow.angle = 0;

            await this.game.vo.playVO('CYB_Level1Tutorial_4', true);
            if (this.shouldPlayAD)
            {
                await this.game.vo.playVO('CYB_Level1Tutorial_AD11', true);
            }
            return;
        }

        // if we were on moveToTarget (updated to pickup) and are still on that step
        if (this.tutorialStep === 'pickup' && this.grid.contents[this.player.coord.x][this.player.coord.y]?.state === Discoverable.State.Located)
        {
            // point at pinged thing
            this.tutorialArrow.visible = true;
            let target = this.pickup.position;
            this.toLocal(new Point(target.x - 50, target.y - 25), this, this.tutorialArrow.position);
            this.tutorialArrow.angle = 0;

            this.tutorialHighlight = this.pickup;
            this.tutorialHighlight.highlight = true;

            await this.game.vo.playVO('CYB_Level1Tutorial_10', true);

            if (this.shouldPlayAD)
            {
                await this.game.vo.playVO(['CYB_Level1Tutorial_AD16', 'CYB_Level1Tutorial_AD17'], true);
            }
        }

        if (wasCoordY)
        {
            await this.game.vo.playVO('CYB_Level4Tutorial_4', true);

            await this.game.vo.playVO('CYB_Level4Tutorial_6', true);

            if (this.tutorialName)
            {
                this.game.logEvent("tutorial_complete", this.tutorialName);
                this.tutorialName = "";
            }
            this.playTutorialEnd();
        }
    }

    private async doPickup()
    {
        this.inactivityTimer = 0;
        FocusManager.instance.clearFocus();

        if (this.pinging || this.player.isMoving)
        {
            return;
        }

        // bail if tutorial is blocking us
        if (this.allowedAction && this.allowedAction !== 'pickup')
        {
            if (this.repeatLine)
            {
                this.triggerRepeat();
            }
            return;
        }

        this.game.vo.stop();

        const {x, y} = this.player.coord;
        const found = this.grid.contents[x][y];
        if (found && found.state === Discoverable.State.Located)
        {
            this.grid.highlights[x][y].show = false;
            this.enabled = false;
            found.state = Discoverable.State.Found;

            let complete = false;
            if (this.tutorialStep === 'pickup')
            {
                complete = true;
            }
            // player loses the level if out of battery and nothing to pick up
            const outOfBatteryLoss =
                // battery is enabled and out
                this.battery.visible && this.battery.level === 0 &&
                // and this was the last located item
                this.locatedCount === 0 &&
                // and we didn't find a battery
                found.type !== Discoverable.Type.Battery &&
                // and we aren't picking up the last part
                (found.type !== Discoverable.Type.Part || this.partsFound?.count < 2);
            if (outOfBatteryLoss)
            {
                complete = true;
            }

            let voProm: Promise<any>;
            // don't want this VO interrupted because we only enable the game after it
            if (found.type === Discoverable.Type.Battery)
            {
                this.battery.level += GAMEPLAY.CHARGES_PER_BATTERY_FOUND;
                voProm = this.game.vo.playVO('CYB_Game_FoundBattery', true)
                    .then(() => this.game.vo.playVO(['CYB_Game_BatteryIncrease', `CYB_Game_${this.battery.level}`], false));
            }
            else if (found.type === Discoverable.Type.Distractor)
            {
                const vo = ['CYB_Game_Distractor', found.foundVO];
                if (!outOfBatteryLoss)
                {
                    vo.push('CYB_Game_DistractorFoundB');
                }
                voProm = this.game.vo.playVO(vo, true);
            }
            else
            {
                if (this.tutorialStep === 'pickup')
                {
                    voProm = Promise.resolve();
                    if (this.shouldPlayAD)
                    {
                        voProm = this.game.vo.playVO('CYB_Level1Tutorial_AD18', true);
                    }
                    voProm = voProm.then(() => this.game.vo.playVO('CYB_Level1Tutorial_14', true));
                }
                else
                {
                    this.partsFound?.findPart(found.name);
                    const count = this.partsFound?.count ?? 0;
                    const REMAINING = [
                        'CYB_Game_MachineParts_Done',
                        'CYB_Game_MachineParts_1Remaining',
                        'CYB_Game_MachineParts_2Remaining'
                    ];
                    voProm = this.game.vo.playVO([found.foundVO, REMAINING[3 - count]], true);
                    if (count === 3)
                    {
                        complete = true;
                    }
                }
            }
            // wait for the found anim to open
            await this.findAnim.open(found);
            // wait a bit to show off the item
            await wait(GAMEPLAY.FOUND_SHOW_TIME);
            // close animation
            await this.findAnim.close();
            // if not complete, can re-enable the game now
            if (!complete)
            {
                this.enabled = true;
            }
            // now make sure the VO completes before we play more VO
            await voProm;
            if (this.tutorialStep === 'pickup')
            {
                this.clearTutorialStep();
                if (this.tutorialName)
                {
                    this.game.logEvent("tutorial_complete", this.tutorialName);
                    this.tutorialName = "";
                }
                await this.playTutorialEnd();
                this.endGame('tutorial');
                return;
            }

            if (!complete)
            {
                if (this.shouldPlayAD)
                {
                    await this.announcePlayerPos();
                }
            }
            else
            {
                this.endGame(!outOfBatteryLoss);
            }
        }
        else
        {
            // if there is a located item, tell them to go pick it up, if hints are enabled
            const located = this.grid.getContents(d => d.state === Discoverable.State.Located);
            if (located.length && this.hintLevel !== Settings.HintFrequency.NONE)
            {
                const item = located[randInt(0, located.length - 1)];
                const square = getCoordVO(item.coord.x, item.coord.y);
                // flip flop between the two options
                if (++this.emptyPickupCount % 2)
                {
                    this.game.vo.playVO(['CYB_Hint_3A', ...square, 'CYB_Hint_3B'], false);
                }
                else
                {
                    this.game.vo.playVO(['CYB_Hint_4', ...square], false);
                }
                return;
            }
            await this.game.vo.playVO('CYB_Game_NothingFound', true);
        }
    }

    private get enabled() { return this.pickup.enabled; }
    private set enabled(enabled: boolean)
    {
        if (!enabled && this.tutorialHighlight)
        {
            this.tutorialHighlight.highlight = false;
            this.tutorialHighlight = null;
        }
        this.pickup.enabled = enabled;
        if (this.coordMove)
        {
            this.coordMove.enabled = enabled;
        }
        this.compass.enabled = enabled;
        this.hint.enabled = enabled;
        this.settings.enabled = enabled;
        this.restArea.switch.interactive = enabled;
        this.restArea.label.interactive = enabled;
        if(this.levelData.TUTORIAL !== 'basics')
        {
            this.battery.interactive = enabled;
            this.partsFound.interactive = enabled;

            if(!(this.levelData.COORD_MOVE && this.game.settings.allowCoordinateEntry.value))
            {
                for(let i = 0; i < this.grid.highlights.length; i++)
                {
                    for(let j = 0; j < this.grid.highlights.length; j++)
                    {
                        this.grid.highlights[i][j].interactive = enabled;
                    }
                }
            }
        }
        
    }

    private backgroundHandler(show:boolean)
    {
        this.background.visible = show;
        this.noBackground.visible = !show;
    }

    private batteryHandler(shouldUse: boolean)
    {
        this.battery.visible = this.levelData.TUTORIAL === 'basics' || shouldUse;
        this.battery.skip = !this.battery.visible;
    }

    private get locatedCount(): number
    {
        return this.grid.getContents(d => d.state === Discoverable.State.Located).length;
    }

    private get hintLevel() { return this.game.settings.hintFrequency.value; }

    private async endGame(success:boolean|'tutorial')
    {
        this.enabled = false;
        this.endOfGame = true;
        this.game.settings.gridGameScene.value = false;

        if (success === true)
        {
            this.wallOverlay.texture = this.loader.getTexture('CrashedWall');
            this.wallOverlay.visible = true;
            this.wallButton.visible = true;
            this.wallButtonClicked = new ExtPromise();
            FocusManager.instance.activateContext([this.wallButton]);

            this.disposables.add(new Quake(this.wallOverlay));
            // TODO: play some VO (including AD)
            this.game.vo.playVO([CHEER[this.levelData.CHARACTER], `CYB_L${this.levelNum}_End`], true)
            .then(() => {
                this.wallButton.highlight = true;
            })
            .then(() => {
                if (this.shouldPlayAD)
                {
                    return this.game.vo.playVO('CYB_Game_Walls_AD');
                }
            });

            this.crashMusic.play(false);

            // adjust crash effect sfx volume 
            this.crashEffect.sound.volume = this.initialVolume;
            if(this.levelData.WALL == 'ice')
            {
                this.crashEffect.sound.volume *= 1.7;
            }
            else if(this.levelData.WALL == 'donut')
            {
                this.crashEffect.sound.volume *= 1.7;
            }
            else if(this.levelData.WALL == 'log')
            {
                this.crashEffect.sound.volume *= 0.5;
            }
            else if(this.levelData.WALL == 'metal')
            {
                this.crashEffect.sound.volume *= 0.5;
            }
            this.crashEffect.play();

            await this.wallButtonClicked.promise;

            this.wallButton.highlight = false;
            this.game.vo.stop();
            FocusManager.instance.popContext();
        }
        else if (success === false)
        {
            await this.game.vo.playVO(['CYB_Game_Transition1', 'CYB_Hint_19'], true);
        }

        if (success === 'tutorial')
        {
            if (this.tutorialName)
            {
                this.game.logEvent("tutorial_complete", this.tutorialName);
                this.tutorialName = "";
            }
            this.game.nextLevel(1);
        }
        else
        {
            this.game.endLevel(success);
        }
    }

    protected playSwitchHover() : Promise<void>
    {
        let ret:Promise<void>;
        const vo = {
            title: "CYB_Panel_Dwell",
            explain: 'CYB_Panel_DwellSettings',
            settings: 'CYB_Panel3_DwellSelected',
        }
        const title_vo = vo.title;
        if( this.game.settings.enableDwellClicks.value ) {
            ret = this.game.vo.playHighlight( [title_vo, 'CYB_Panel1_On'], false );
        } else {
            ret = this.game.vo.playHighlight( [title_vo, 'CYB_Panel1_Off'], false );
        }
        return ret;
    }

    protected playSwitchVO() : Promise<void>
    {
        let ret:Promise<void>;
        const vo = {
            title: "CYB_Panel_Dwell",
            explain: 'CYB_Panel_DwellSettings',
            settings: 'CYB_Panel3_DwellSelected',
        }
        const title_vo = vo.title;
        if( this.game.settings.enableDwellClicks.value ) {
            ret = this.game.vo.playVO( [title_vo, 'CYB_Panel1_On'], false );
        } else {
            ret = this.game.vo.playVO( [title_vo, 'CYB_Panel1_Off'], false );
        }
        return ret;
    }

    cleanup()
    {
        KeyboardJS.setContext('global');
        super.cleanup();
        FocusManager.instance.popContext();
        this.hammer.destroy();
        this.disposables.destroy();

        super.destroy({children: true});
    }
}
